import React, { useState, useEffect } from 'react';
import AdminBackLink from '../components/AdminBackLink';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../services/api';
import api from '../config/api';
import { User, UserRole } from '../types';
import auth from '../utils/auth';
import { HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi';

const Users: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: UserRole.DASHBOARD_ADMIN,
  });

  useEffect(() => {
    // Check if user is super admin
    const user = auth.getUser();
    if (!user || user.role !== UserRole.SUPER_ADMIN) {
      navigate('/dashboard');
      return;
    }
    fetchUsers();
  }, [navigate]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axiosInstance.get(api.auth.getAllUsers);
      setUsers(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'فشل تحميل المستخدمين');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      name: user.name || '',
      email: user.email,
      password: '',
      role: user.role,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المستخدم؟')) return;

    try {
      setError('');
      setSuccess('');
      await axiosInstance.delete(api.auth.deleteUser(id));
      setSuccess('تم حذف المستخدم بنجاح');
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'فشل حذف المستخدم');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');

      if (editingUser) {
        // Update user
        const updateData: any = {
          email: formData.email,
          name: formData.name || undefined,
          role: formData.role,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await axiosInstance.put(api.auth.updateUser(editingUser.id), updateData);
        setSuccess('تم تحديث المستخدم بنجاح');
      } else {
        // Create new user
        const createData: any = {
          email: formData.email,
          password: formData.password,
          role: formData.role,
        };
        if (formData.name) {
          createData.name = formData.name;
        }
        await axiosInstance.post(api.auth.createUser, createData);
        setSuccess('تم إضافة المستخدم بنجاح');
      }

      setShowForm(false);
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: UserRole.DASHBOARD_ADMIN,
      });
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || `فشل ${editingUser ? 'تحديث' : 'إضافة'} المستخدم`);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: UserRole.DASHBOARD_ADMIN,
    });
  };

  const getRoleLabel = (role: UserRole) => {
    switch (role) {
      case UserRole.SUPER_ADMIN:
        return 'سوبر أدمن';
      case UserRole.DASHBOARD_ADMIN:
        return 'داشبورد أدمن';
      default:
        return role;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-paper)' }}>
      <nav
        style={{
          backgroundColor: '#041525',
          borderBottom: '2px solid #477CD7',
          width: '100%',
          height: '72px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex justify-between items-center h-full relative">
            <AdminBackLink />
            <h1 
              className="text-xl font-semibold text-white absolute left-1/2 transform -translate-x-1/2"
              style={{
                fontFamily: 'var(--font-arabtype)',
                letterSpacing: '0.5px',
              }}
            >
              إدارة المستخدمين
            </h1>
            <div className="w-24"></div>
          </div>
        </div>
      </nav>

      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          {/* Messages */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-800 rounded-lg">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg">
              {success}
            </div>
          )}

          {/* Header */}
          <div className="mb-6 flex justify-between items-center">
            <h2 className="text-2xl font-bold" style={{ color: '#ffffff' }}>قائمة المستخدمين</h2>
            <button
              onClick={() => {
                handleCancel();
                setShowForm(true);
              }}
              className="px-6 py-2 rounded-lg text-white transition-opacity hover:opacity-90"
              style={{
                background: 'var(--button-gradient)',
              }}
            >
              إضافة مستخدم جديد
            </button>
          </div>

          {/* Form Modal */}
          {showForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">
                      {editingUser ? 'تعديل المستخدم' : 'إضافة مستخدم جديد'}
                    </h2>
                    <button
                      onClick={handleCancel}
                      className="text-gray-400 hover:text-gray-600 text-2xl"
                    >
                      ×
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        الاسم
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
                        style={{
                          borderColor: 'var(--border-main)',
                        } as React.CSSProperties}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        البريد الإلكتروني *
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
                        style={{
                          borderColor: 'var(--border-main)',
                        } as React.CSSProperties}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        كلمة المرور {editingUser ? '(اتركه فارغاً للحفاظ على كلمة المرور الحالية)' : '*'}
                      </label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required={!editingUser}
                        className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
                        style={{
                          borderColor: 'var(--border-main)',
                        } as React.CSSProperties}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        الدور *
                      </label>
                      <select
                        value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
                        className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
                        style={{
                          borderColor: 'var(--border-main)',
                        } as React.CSSProperties}
                      >
                        <option value={UserRole.SUPER_ADMIN}>سوبر أدمن</option>
                        <option value={UserRole.DASHBOARD_ADMIN}>داشبورد أدمن</option>
                      </select>
                    </div>

                    <div className="flex gap-4 justify-end pt-4">
                      <button
                        type="button"
                        onClick={handleCancel}
                        className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                      >
                        إلغاء
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-2 rounded-lg text-white transition-opacity hover:opacity-90"
                        style={{
                          background: 'var(--button-gradient)',
                        }}
                      >
                        {editingUser ? 'تحديث' : 'إضافة'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* جدول المستخدمين */}
          {loading ? (
            <div className="text-center py-12" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>جاري التحميل...</div>
          ) : users.length === 0 ? (
            <div className="text-center py-12" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>لا يوجد مستخدمين</div>
          ) : (
            <div className="rounded-lg overflow-hidden" style={{ border: '1px solid rgba(255, 255, 255, 0.1)', background: 'rgba(8, 42, 73, 0.6)' }}>
              <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.15)' }}>
                    <th style={{ padding: '14px 16px', textAlign: 'right', color: 'rgba(255,255,255,0.9)', fontWeight: 600, fontSize: '14px' }}>الاسم / البريد</th>
                    <th style={{ padding: '14px 16px', textAlign: 'right', color: 'rgba(255,255,255,0.9)', fontWeight: 600, fontSize: '14px' }}>الدور</th>
                    <th style={{ padding: '14px 16px', textAlign: 'right', color: 'rgba(255,255,255,0.9)', fontWeight: 600, fontSize: '14px' }}>تاريخ الإنشاء</th>
                    <th style={{ padding: '14px 16px', textAlign: 'center', color: 'rgba(255,255,255,0.9)', fontWeight: 600, fontSize: '14px' }}>إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
                      <td style={{ padding: '14px 16px', color: '#ffffff', fontSize: '15px' }}>
                        <div>{user.name || '—'}</div>
                        <div className="admin-user-email" style={{ color: 'rgba(255,255,255,0.7)' }}>{user.email}</div>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span
                          className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full"
                          style={{
                            backgroundColor: user.role === UserRole.SUPER_ADMIN ? 'rgba(71, 124, 215, 0.25)' : 'rgba(104, 148, 222, 0.2)',
                            color: user.role === UserRole.SUPER_ADMIN ? '#93c5fd' : '#A5B4FC',
                          }}
                        >
                          {getRoleLabel(user.role)}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', color: 'rgba(255,255,255,0.8)', fontSize: '14px' }}>{formatDate(user.createdAt)}</td>
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <div className="flex gap-2 items-center justify-center">
                          <button
                            onClick={() => handleEdit(user)}
                            className="p-2 rounded-lg transition-all hover:bg-blue-500 hover:bg-opacity-20"
                            style={{ color: '#477CD7' }}
                            title="تعديل"
                          >
                            {React.createElement(HiOutlinePencil as any, { size: 18 })}
                          </button>
                          <button
                            onClick={() => handleDelete(user.id)}
                            className="p-2 rounded-lg transition-all hover:bg-red-500 hover:bg-opacity-20"
                            style={{ color: '#EF4444' }}
                            title="حذف"
                          >
                            {React.createElement(HiOutlineTrash as any, { size: 18 })}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Users;



